import React, { useCallback, useState } from 'react';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import styles from './product-ingredients.module.scss';
import {
  useThrottledCallback,
  v2_ProductDetail,
  v2_selectMultipleChoiceConfigurationPropertyValue,
  v2_useProduct,
} from '@crocodille/common';

const IngredientRow = ({
  name,
  value,
  min = 0,
  max,
  onChange,
}: {
  name: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) => {
  const [draftValue, setDraftValue] = useState<number | null>(null);

  const commitDraft = useCallback(() => {
    if (draftValue !== null) {
      if (draftValue < min) {
        onChange(min);
      } else if (max !== undefined && draftValue > max) {
        onChange(max);
      } else {
        onChange(draftValue);
      }
    }

    setDraftValue(null);
  }, [draftValue, onChange, min, max]);

  const displayValue = draftValue ?? value;

  const throttledCommitDraft = useThrottledCallback(commitDraft, 100);

  const canDecrement = displayValue !== undefined && displayValue > min;
  const canIncrement =
    displayValue !== undefined && (max === undefined || displayValue < max);

  return (
    <tr>
      <td>{name}</td>
      <td>
        <div className={styles.quantityContainer}>
          <button
            type="button"
            className={cn(styles.button, styles.decrease)}
            disabled={!canDecrement}
            onClick={() => {
              if (canDecrement) {
                setDraftValue(0);
                throttledCommitDraft();
              }
            }}
          >
            <span>-</span>
          </button>
          <span className={styles.value}>{value}</span>
          <button
            type="button"
            className={cn(styles.button, styles.increase)}
            disabled={!canIncrement}
            onClick={() => {
              if (canIncrement) {
                setDraftValue(1);
                throttledCommitDraft();
              }
            }}
          >
            <span>+</span>
          </button>
        </div>
      </td>
    </tr>
  );
};

interface ProductIngredientsProps {
  readonly product: v2_ProductDetail;
  readonly configurationValues: ReturnType<
    typeof v2_useProduct
  >['configurationValues'];
  setMultipleChoiceProperty(
    propertyId: string,
    option: string,
    quantity: number,
  ): void;
}

const ProductIngredients: React.FC<
  React.PropsWithChildren<ProductIngredientsProps>
> = ({ product, configurationValues, setMultipleChoiceProperty }) => {
  const { t } = useTranslation();

  const ingredients = product.configuration.find(
    (p) => p.role === 'remove_ingredients',
  )?.options;

  const values = v2_selectMultipleChoiceConfigurationPropertyValue(
    configurationValues,
    'remove_ingredients',
  );

  return (
    <div className={styles.container}>
      <div className={styles.title}>{t('product.ingredients.modifying')}</div>
      <table className={styles.table}>
        <tbody>
          {ingredients?.map((ingredient) => (
            <IngredientRow
              key={ingredient.id}
              value={values[ingredient.itemCode] ?? 1}
              min={0}
              max={ingredient.maxSelections ?? Infinity}
              name={ingredient.name}
              onChange={(value) =>
                setMultipleChoiceProperty(
                  'remove_ingredients',
                  ingredient.itemCode,
                  value,
                )
              }
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductIngredients;
